// frontend/src/pages/admin/AdminBulkRequests.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { bulkApi } from '../../api/bulk';
import helpers from '../../utils/helpers';
import { 
    FaSearch, FaFilter, FaBoxOpen, FaEye, FaCheckCircle, FaTimesCircle, 
    FaArrowLeft, FaSpinner, FaPlus, FaTrash, FaInfoCircle 
} from 'react-icons/fa';

function AdminBulkRequests() {
  const [view, setView] = useState('list'); // list, create, detail
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // --- BULK DELETE STATE ---
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  // -----------------------------

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Create Form State
  const [newItems, setNewItems] = useState([
    { part_name: '', quantity: 1, target_price: '', notes: '' }
  ]);
  const [requestNotes, setRequestNotes] = useState('');

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    filterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests, filterStatus, searchTerm]);

  const fetchRequests = async () => {
    try {
      const data = await bulkApi.getAllRequestsAdmin();
      setRequests(data);
    } catch (err) {
      console.error("Failed to load requests", err);
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    let result = requests;

    if (filterStatus !== 'all') {
      result = result.filter(r => r.status === filterStatus);
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      // Older requests can have an empty name or email - treat those as ''
      // instead of crashing the page.
      result = result.filter(r =>
        (r.request_id || '').toLowerCase().includes(lower) ||
        (r.user_email || '').toLowerCase().includes(lower) ||
        (r.user_name || '').toLowerCase().includes(lower) ||
        (r.user_phone || '').toLowerCase().includes(lower)
      );
    }
    setFilteredRequests(result);
    setSelectedIds([]); // Clear selection when filters change
  };

  // --- BULK DELETE HANDLERS ---
  const handleSelectAll = (e) => {
      if (e.target.checked) {
          const allIds = filteredRequests.map(r => r.id);
          setSelectedIds(allIds);
      } else {
          setSelectedIds([]);
      }
  };

  const handleSelectOne = (id) => {
      setSelectedIds(prev => {
          if (prev.includes(id)) {
              return prev.filter(item => item !== id);
          } else {
              return [...prev, id];
          }
      });
  };

  const handleBulkDelete = async () => {
      if (selectedIds.length === 0) return;
      if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} bulk request(s)?`)) return;

      setIsBulkDeleting(true);
      try {
          await bulkApi.deleteRequestsAdmin(selectedIds);
          alert("Selected requests deleted successfully.");
          setSelectedIds([]);
          fetchRequests();
      } catch (err) {
          alert("Failed to delete requests.");
      } finally {
          setIsBulkDeleting(false);
      }
  };
  // ----------------------------

  // --- CREATE FORM HANDLERS ---
  const handleItemChange = (index, field, value) => {
    const updated = [...newItems];
    updated[index][field] = value;
    setNewItems(updated);
  };

  const addItemRow = () => {
    setNewItems([...newItems, { part_name: '', quantity: 1, target_price: '', notes: '' }]);
  };

  const removeItemRow = (index) => {
    if (newItems.length === 1) return;
    setNewItems(newItems.filter((_, i) => i !== index));
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    
    // Validation
    if (newItems.some(i => !i.part_name.trim())) {
        alert("Please enter a Part Name for all items.");
        return;
    }

    setSubmitting(true);
    try {
      const payload = {
        items: newItems.map(i => ({
            ...i,
            quantity: parseInt(i.quantity),
            target_price: i.target_price ? parseFloat(i.target_price) : null
        })),
        notes: requestNotes
      };

      await bulkApi.createRequest(payload);
      alert("Bulk request submitted successfully!");
      setView('list');
      setNewItems([{ part_name: '', quantity: 1, target_price: '', notes: '' }]);
      setRequestNotes('');
      fetchRequests();
    } catch (err) {
      alert("Failed to submit request.");
    } finally {
      setSubmitting(false);
    }
  };

  // --- DETAIL VIEW HANDLERS ---
  const handleViewDetail = (req) => {
      setSelectedRequest(req);
      setView('detail');
  };

  const handleStatusUpdate = async (status) => {
      if (!window.confirm(`Are you sure you want to ${status.toUpperCase()} this quote?`)) return;
      setSubmitting(true);
      try {
          await bulkApi.updateStatus(selectedRequest.id, status);
          alert(`Quote ${status} successfully.`);
          setView('list');
          fetchRequests();
      } catch (err) {
          alert("Action failed.");
      } finally {
          setSubmitting(false);
      }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      quoted: "bg-blue-100 text-blue-800",
      accepted: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800"
    };
    return <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${styles[status] || 'bg-gray-100'}`}>{status}</span>;
  };

  if (loading) return <div className="text-center py-10">Loading requests...</div>;

  // --- VIEW 2: CREATE ---
  if (view === 'create') {
      return (
          <div>
              <button onClick={() => setView('list')} className="text-gray-500 hover:text-primary flex items-center gap-2 mb-4">
                  <FaArrowLeft /> Back to List
              </button>
              <h2 className="text-xl font-bold text-gray-800 mb-4">Create Bulk Request</h2>
              
              <div className="bg-blue-50 p-4 rounded mb-6 text-sm text-blue-800">
                  <p>List the parts you need below. If you have a target price per unit, let us know. We will review and send you a formal quote.</p>
              </div>

              <form onSubmit={handleSubmitRequest}>
                  <div className="space-y-4 mb-6">
                      {newItems.map((item, index) => (
                          <div key={index} className="flex flex-col md:flex-row gap-2 items-start md:items-center bg-gray-50 p-3 rounded border border-gray-200">
                              <div className="flex-grow w-full">
                                  <input 
                                      placeholder="Part Name / Number *" 
                                      value={item.part_name}
                                      onChange={(e) => handleItemChange(index, 'part_name', e.target.value)}
                                      className="w-full p-2 border rounded"
                                      required
                                  />
                              </div>
                              <div className="w-full md:w-24">
                                  <input 
                                      type="number" 
                                      placeholder="Qty" 
                                      value={item.quantity}
                                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                      className="w-full p-2 border rounded"
                                      min="1"
                                      required
                                  />
                              </div>
                              <div className="w-full md:w-32">
                                  <input 
                                      type="number" 
                                      placeholder="Target Price" 
                                      value={item.target_price}
                                      onChange={(e) => handleItemChange(index, 'target_price', e.target.value)}
                                      className="w-full p-2 border rounded"
                                  />
                              </div>
                              <div className="w-full md:w-48">
                                  <input 
                                      placeholder="Notes (Optional)" 
                                      value={item.notes}
                                      onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                                      className="w-full p-2 border rounded"
                                  />
                              </div>
                              <button 
                                  type="button" 
                                  onClick={() => removeItemRow(index)}
                                  className="text-red-500 hover:text-red-700 p-2"
                                  disabled={newItems.length === 1}
                              >
                                  <FaTrash />
                              </button>
                          </div>
                      ))}
                  </div>

                  <button type="button" onClick={addItemRow} className="text-primary font-bold text-sm flex items-center gap-2 mb-6 hover:underline">
                      <FaPlus /> Add Another Item
                  </button>

                  <div className="mb-6">
                      <label className="block text-sm font-bold text-gray-700 mb-1">Additional Notes</label>
                      <textarea 
                          rows="3" 
                          value={requestNotes}
                          onChange={(e) => setRequestNotes(e.target.value)}
                          className="w-full p-2 border rounded"
                          placeholder="Any specific instructions for this order..."
                      ></textarea>
                  </div>

                  <button 
                      type="submit" 
                      disabled={submitting}
                      className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-3 rounded transition duration-300 flex justify-center items-center gap-2"
                  >
                      {submitting ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />} Submit Request
                  </button>
              </form>
          </div>
      );
  }

  // --- VIEW 3: DETAIL ---
  if (view === 'detail' && selectedRequest) {
      return (
          <div>
              <button onClick={() => setView('list')} className="text-gray-500 hover:text-primary flex items-center gap-2 mb-4">
                  <FaArrowLeft /> Back to List
              </button>
              
              {/* UPDATED HEADER: Stack on mobile */}
              <div className="flex flex-col sm:flex-row justify-between items-start mb-6 gap-4">
                  <div>
                      <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                          Request {selectedRequest.request_id}
                          {getStatusBadge(selectedRequest.status)}
                      </h2>
                      <p className="text-sm text-gray-500">Created: {new Date(selectedRequest.created_at).toLocaleString()}</p>
                  </div>
                  {selectedRequest.status === 'quoted' && (
                      <div className="flex gap-2 w-full sm:w-auto">
                          <button 
                              onClick={() => handleStatusUpdate('accepted')}
                              disabled={submitting}
                              className="flex-1 sm:flex-none bg-green-600 text-white px-4 py-2 rounded font-bold hover:bg-green-700 flex items-center justify-center gap-2"
                          >
                              <FaCheckCircle /> Accept
                          </button>
                          <button 
                              onClick={() => handleStatusUpdate('rejected')}
                              disabled={submitting}
                              className="flex-1 sm:flex-none bg-red-100 text-red-600 px-4 py-2 rounded font-bold hover:bg-red-200 flex items-center justify-center gap-2"
                          >
                              <FaTimesCircle /> Reject
                          </button>
                      </div>
                  )}
              </div>

              {/* Items Table */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 mb-6">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                            <tr>
                                <th className="p-3">Item</th>
                                <th className="p-3 text-center">Qty</th>
                                <th className="p-3 text-right">Target Price</th>
                                <th className="p-3 text-right bg-blue-50 text-blue-800">Quoted Unit</th>
                                <th className="p-3 text-right bg-blue-50 text-blue-800">Line Total</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {selectedRequest.items.map((item, idx) => (
                                <tr key={idx}>
                                    <td className="p-3">
                                        <div className="font-bold text-gray-800">{item.part_name}</div>
                                        {item.notes && <div className="text-xs text-gray-500 italic">{item.notes}</div>}
                                    </td>
                                    <td className="p-3 text-center">{item.quantity}</td>
                                    <td className="p-3 text-right text-gray-500">
                                        {item.target_price ? helpers.formatCurrency(item.target_price, 'NGN') : '-'}
                                    </td>
                                    <td className="p-3 text-right bg-blue-50 font-medium">
                                        {item.quoted_unit_price ? helpers.formatCurrency(item.quoted_unit_price, 'NGN') : <span className="text-gray-400">-</span>}
                                    </td>
                                    <td className="p-3 text-right bg-blue-50 font-bold text-gray-800">
                                        {item.line_total ? helpers.formatCurrency(item.line_total, 'NGN') : <span className="text-gray-400">-</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        {selectedRequest.total_quote_amount > 0 && (
                            <tfoot className="bg-blue-100">
                                <tr>
                                    <td colSpan="4" className="p-3 text-right font-bold text-blue-900">Total Quote:</td>
                                    <td className="p-3 text-right font-bold text-blue-900 text-lg">
                                        {helpers.formatCurrency(selectedRequest.total_quote_amount, 'NGN')}
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                  </div>
              </div>

              {selectedRequest.admin_notes && (
                  <div className="bg-yellow-50 p-4 rounded border border-yellow-200 mb-6">
                      <h4 className="font-bold text-yellow-800 text-sm mb-1">Note from Admin:</h4>
                      <p className="text-yellow-700 text-sm">{selectedRequest.admin_notes}</p>
                  </div>
              )}

              {selectedRequest.status === 'accepted' && (
                  <div className="bg-green-50 p-4 rounded border border-green-200 text-center">
                      <FaCheckCircle className="text-green-500 text-3xl mx-auto mb-2" />
                      <h3 className="font-bold text-green-800 text-lg">Quote Accepted!</h3>
                      <p className="text-green-700 text-sm mb-4">The customer has accepted this quote.</p>
                  </div>
              )}
          </div>
      );
  }

  // --- VIEW 1: LIST ---
  return (
    <>
      {/* ACTIONS BAR - Updated for Mobile Responsiveness */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <FaBoxOpen className="text-primary" /> Bulk Purchases
        </h2>
        <div className="flex gap-2 w-full sm:w-auto">
            <button 
                onClick={() => setView('create')}
                className="bg-primary hover:bg-primary-dark text-white text-sm font-bold py-2 px-4 rounded flex items-center justify-center gap-2 transition duration-300 w-full md:w-auto"
            >
                <FaPlus /> New Request
            </button>
            {/* BULK DELETE BUTTON (NEW) */}
            {selectedIds.length > 0 && (
                <button 
                    onClick={handleBulkDelete}
                    disabled={isBulkDeleting}
                    className="bg-red-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2 hover:bg-red-700 transition-colors shadow-sm w-full md:w-auto justify-center"
                >
                    {isBulkDeleting ? <FaSpinner className="animate-spin" /> : <FaTrash />} 
                    Delete Selected ({selectedIds.length})
                </button>
            )}
        </div>
      </div>

      {/* --- TOOLKIT --- */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg shadow-sm flex items-start gap-3">
          <FaInfoCircle className="text-blue-500 text-xl mt-1 flex-shrink-0" />
          <div>
              <h4 className="font-bold text-blue-800 text-sm">Get Custom Quotes</h4>
              <p className="text-sm text-blue-700">
                  Need parts in large quantities? Submit a list of items and your target price. We will review and send you a competitive quote.
              </p>
          </div>
      </div>

      {/* FILTERS & SEARCH */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6 flex flex-col md:flex-row gap-4 justify-between items-center">
        <div className="flex items-center gap-2 w-full md:w-auto">
          <FaFilter className="text-gray-400" />
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-primary"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending Quote</option>
            <option value="quoted">Quoted</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className="relative w-full md:w-64">
          <input 
            type="text" 
            placeholder="Search ID, User, Email..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-primary"
          />
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider border-b border-gray-200">
                {/* SELECT ALL CHECKBOX */}
                <th className="p-4 w-10">
                    <input 
                        type="checkbox" 
                        onChange={handleSelectAll}
                        checked={filteredRequests.length > 0 && selectedIds.length === filteredRequests.length}
                        className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary cursor-pointer"
                    />
                </th>
                <th className="p-4">Request ID</th>
                <th className="p-4">Customer</th>
                <th className="p-4">Items</th>
                <th className="p-4">Total Value</th>
                <th className="p-4">Status</th>
                <th className="p-4">Date</th>
                <th className="p-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredRequests.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-gray-500">No requests match your filters.</td>
                </tr>
              ) : (
                filteredRequests.map(req => (
                  <tr key={req.id} className="hover:bg-gray-50">
                    {/* ROW CHECKBOX */}
                    <td className="p-4">
                        <input 
                            type="checkbox" 
                            checked={selectedIds.includes(req.id)}
                            onChange={() => handleSelectOne(req.id)}
                            className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary cursor-pointer"
                        />
                    </td>
                    <td className="p-4 font-mono font-bold text-primary">
                      <Link to={`/admin/bulk/${req.id}`}>{req.request_id}</Link>
                    </td>
                    <td className="p-4 text-sm">
                      <div className="font-bold text-gray-800">{req.user_name || req.user_phone || 'Unknown customer'}</div>
                      <div className="text-xs text-gray-500">
                        {(req.user_email || '').endsWith('@phone.user') ? '' : req.user_email}
                      </div>
                      {req.user_phone && <div className="text-xs text-gray-500">{req.user_phone}</div>}
                    </td>
                    <td className="p-4 text-sm">
                      {(req.items || []).length} {(req.items || []).length === 1 ? 'Item' : 'Items'}
                    </td>
                    <td className="p-4 text-sm font-bold text-gray-800">
                      {req.total_quote_amount > 0 ? helpers.formatCurrency(req.total_quote_amount, 'NGN') : '-'}
                    </td>
                    <td className="p-4">{getStatusBadge(req.status)}</td>
                    <td className="p-4 text-sm text-gray-400">{new Date(req.created_at).toLocaleDateString()}</td>
                    <td className="p-4 text-right">
                      <Link 
                        to={`/admin/bulk/${req.id}`}
                        className="bg-gray-100 text-gray-600 p-2 rounded hover:bg-gray-200 inline-block"
                        title="View Detail"
                      >
                        <FaEye />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default AdminBulkRequests;